import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ShoppingCart, Trash2, Plus, ArrowRight, Shield, FileText, Zap, Mail, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useState } from 'react';
import { trackAddonSelected } from '@/lib/analytics';

export default function CartPage() {
  const { items, removeItem, updateItem, getSubtotal, getTotal, clearCart,
          couponCode, setCouponCode, couponDiscount, setCouponDiscount, couponInfo, setCouponInfo } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useSeoMeta({ seo_title: 'Your Cart - The Fashion Detective' });

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    try {
      const res = await api.post('/orders/validate-coupon', {
        code: couponCode.trim(),
        subtotal_cents: getSubtotal(),
      });
      setCouponDiscount(res.data.estimated_discount_cents || 0);
      setCouponInfo(res.data);
      toast.success(`Coupon applied! -$${(res.data.estimated_discount_cents / 100).toFixed(2)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid coupon');
      setCouponDiscount(0);
      setCouponInfo(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/account/sign-in?redirect=/order/checkout');
      return;
    }
    navigate('/order/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
        <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
        <h1 className="font-display text-2xl font-medium text-ink mb-2">Your Cart is Empty</h1>
        <p className="text-sm text-muted-foreground mb-6">Add items for authentication or appraisal to get started.</p>
        <Link to="/services">
          <Button className="gap-2 rounded-[12px]">
            <Plus className="h-4 w-4" /> Start an Order
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page">
      <section style={{ background: 'linear-gradient(135deg, hsl(36 33% 98%) 0%, hsl(36 22% 94%) 55%, hsl(35 19% 88%) 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-[-0.02em] text-ink mb-1">
            Your Cart
          </h1>
          <p className="text-sm text-muted-foreground">{items.length} item{items.length !== 1 ? 's' : ''}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* Cart Items */}
          <div className="space-y-4">
            {items.map((item, idx) => {
              let price = item.base_price_cents || (item.service_family === 'authentication' ? 2000 : 5000);
              if (item.is_expedited && item.service_family === 'authentication') price += 2000;
              if (item.wants_mailed_document) price += 1000;

              return (
                <motion.div
                  key={item.cartId}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Card className="bg-white border-stone/50">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {item.service_family === 'authentication'
                              ? <Shield className="h-4 w-4" style={{ color: '#0E6B5D' }} />
                              : <FileText className="h-4 w-4" style={{ color: '#A36A3A' }} />
                            }
                            <span className="text-sm font-medium text-ink">
                              {item.service_family === 'authentication' ? 'Authentication' : 'Appraisal'}
                            </span>
                            <Badge variant="outline" className="text-[10px]">{item.category_name}</Badge>
                          </div>
                          <p className="text-sm text-ink font-medium">{item.item_title || 'Untitled Item'}</p>
                          {item.brand_name && <p className="text-xs text-muted-foreground">{item.brand_name}</p>}
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-ink">${(price / 100).toFixed(2)}</span>
                          <button
                            onClick={() => removeItem(item.cartId)}
                            className="block mt-1 text-xs text-muted-foreground hover:text-destructive"
                            data-testid={`remove-item-${idx}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 inline mr-0.5" /> Remove
                          </button>
                        </div>
                      </div>

                      {/* Add-ons */}
                      <Separator className="my-3" />
                      <div className="space-y-2">
                        {item.service_family === 'authentication' && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Zap className="h-3.5 w-3.5" style={{ color: '#A36A3A' }} />
                              <div>
                                <span className="text-xs">Expedited Review (+$20)</span>
                                <p className="text-[9px] text-muted-foreground">Results in under 2 hours during business hours (Mon–Fri 9am–5pm EST)</p>
                              </div>
                            </div>
                            <Switch
                              checked={item.is_expedited}
                              onCheckedChange={(v) => { updateItem(item.cartId, { is_expedited: v }); if (v) trackAddonSelected('expedited', item.category_slug); }}
                              data-testid={`expedited-toggle-${idx}`}
                            />
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5" style={{ color: '#0E6B5D' }} />
                            <div>
                              <span className="text-xs">Mailed Certificate of Authenticity (+$10)</span>
                              <p className="text-[9px] text-muted-foreground">Physical certificate mailed to US addresses. 5–7 business days.</p>
                            </div>
                          </div>
                          <Switch
                            checked={item.wants_mailed_document}
                            onCheckedChange={(v) => { updateItem(item.cartId, { wants_mailed_document: v }); if (v) trackAddonSelected('mailed_document', item.category_slug); }}
                            data-testid={`mailed-doc-toggle-${idx}`}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}

            <Link to="/order/submit" className="block">
              <Button variant="outline" className="w-full gap-2">
                <Plus className="h-4 w-4" /> Add Another Item
              </Button>
            </Link>
          </div>

          {/* Summary */}
          <div>
            <Card className="bg-white border-stone/50 sticky top-20">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-ink mb-4">Order Summary</h3>

                {/* Coupon */}
                <div className="flex gap-2 mb-4">
                  <Input
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value)}
                    placeholder="Coupon code"
                    className="h-9 text-xs"
                    data-testid="coupon-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs"
                    onClick={handleValidateCoupon}
                    disabled={validatingCoupon}
                    data-testid="apply-coupon-btn"
                  >
                    <Tag className="h-3.5 w-3.5 mr-1" /> Apply
                  </Button>
                </div>
                {couponInfo && (
                  <p className="text-xs text-green-700 mb-3">Coupon: {couponInfo.description || couponInfo.code}</p>
                )}

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-ink">${(getSubtotal() / 100).toFixed(2)}</span>
                  </div>
                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>Discount</span>
                      <span>-${(couponDiscount / 100).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax</span>
                    <span className="text-ink">$0.00</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-semibold text-base">
                    <span className="text-ink">Total</span>
                    <span className="text-ink">${(getTotal() / 100).toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  className="w-full mt-5 gap-2 rounded-[12px] h-11 active:scale-[0.98]"
                  onClick={handleCheckout}
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout <ArrowRight className="h-4 w-4" />
                </Button>

                <p className="text-[10px] text-muted-foreground text-center mt-3">
                  No sales tax. Mailed certificate fee refunded if item is counterfeit.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
